"use client";

import { useEffect, useRef, useState } from "react";
import { useIdentity } from "@/lib/api/session";
import { useTelegramMiniAppSignIn } from "@/lib/api/social";
import { describeAccountUnavailable, refusalFromError } from "@/lib/api/signin-outcome";
import { advanceMiniApp, isTelegramIos, once, TELEGRAM_WEB_APP_SCRIPT_ID, type WhoamiState } from "@/lib/telegram/mini-app";
import type { TelegramWebApp } from "@/lib/telegram/types";
import { useToasts } from "@/components/ui/Toast";

/* The casino opened as a Telegram Mini App.

   The layout puts Telegram's bridge script on the page only for a site with
   Telegram sign-in on. Inside Telegram it carries `initData`; in a plain
   browser it is empty and this does nothing at all. Inside, the app goes
   full screen (in try/catch — older Telegram clients lack the call), stops
   iOS swiping it closed on a scroll, tells Telegram it is ready, and signs
   the player in from `initData` if `/whoami` says they are not — once
   (lib/telegram/mini-app.ts). The safe areas Telegram reports are applied in
   CSS from the variables its script sets (globals.css, `--tg-inset-*`). */

export function TelegramMiniApp() {
  const [app, setApp] = useState<TelegramWebApp | null>(null);
  const latch = useRef(once());
  const identity = useIdentity();
  const signIn = useTelegramMiniAppSignIn();
  const toasts = useToasts();

  useEffect(() => {
    const attach = () => {
      const webApp = window.Telegram?.WebApp;

      if (!webApp?.initData) {
        return;
      }

      setApp(webApp);

      try {
        webApp.requestFullscreen?.();
      } catch {
        /* Not supported by this Telegram client. */
      }

      if (isTelegramIos(webApp.platform)) {
        try {
          webApp.disableVerticalSwipes?.();
        } catch {
          /* Not supported by this Telegram client. */
        }
      }

      webApp.ready();
    };

    if (window.Telegram?.WebApp) {
      attach();
      return;
    }

    /* No bridge script on this page: Telegram is off for the site. */
    const script = document.getElementById(TELEGRAM_WEB_APP_SCRIPT_ID);

    if (!script) {
      return;
    }

    script.addEventListener("load", attach, { once: true });
    return () => script.removeEventListener("load", attach);
  }, []);

  const whoami: WhoamiState = identity.isPending
    ? "pending"
    : identity.isError
      ? "error"
      : identity.isSignedIn
        ? "signed-in"
        : "signed-out";

  const { mutate } = signIn;
  const { push } = toasts;

  useEffect(() => {
    advanceMiniApp(latch.current, { initData: app?.initData, whoami }, (initData) =>
      mutate(initData, {
        onError: (error) => {
          const refusal = refusalFromError(error);

          push({
            tone: "error",
            title: "Telegram sign-in failed",
            body:
              refusal.kind === "account-unavailable"
                ? describeAccountUnavailable(refusal.account)
                : refusal.kind === "geoblocked"
                  ? "Sign-in isn't available from your region."
                  : "Open the sign-in to try another way in.",
          });
        },
      }),
    );
  }, [app, whoami, mutate, push]);

  if (!app) {
    return null;
  }

  return (
    <>
      {/* Telegram's controls float over the top inset; the page scrolling
          under them would show through, so the strip is painted. */}
      <div aria-hidden className="fixed inset-x-0 top-0 z-[45] h-[var(--tg-inset-top)] bg-elevation-1" />
      {signIn.isPending && (
        <div
          role="status"
          aria-live="polite"
          className="fixed inset-0 z-[80] grid place-items-center bg-black/70 backdrop-blur-[2px]"
        >
          <p className="rounded-xl border border-border bg-elevation-1 px-[16px] py-[12px] text-[14px] font-bold leading-[1.6] text-fg">
            Signing you in with Telegram…
          </p>
        </div>
      )}
    </>
  );
}
